import { useEffect, useState } from 'react';
import { listEvents, calendarTeamId } from '../../extensions/bundled/calendar/calendarStore';
import { useCalendarUiStore } from '../../extensions/bundled/calendar/calendarUiStore';
import { isTauri } from '../../utils/tauri';
import i18n from '../../i18n';

/**
 * Load the personal calendar's events, re-reading whenever the ui store's
 * eventsVersion bumps (own writes, .ics imports, sync pulls).
 */
export function useCalendarEvents() {
  const eventsVersion = useCalendarUiStore((s) => s.eventsVersion);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listEvents();
        if (!cancelled) setEvents(rows);
      } catch {
        if (!cancelled) setEvents([]); // records backend unavailable (browser dev)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventsVersion]);
  return events;
}

/**
 * Read one .ics file's text: Tauri dialog + readTextFile (already-granted
 * commands; grants are per COMMAND and text reads are covered), or a browser
 * file input outside the desktop app. Returns null when cancelled.
 * (Relocated from the removed CalendarSidebar — fix round 3.)
 */
export async function pickIcsText() {
  if (isTauri()) {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const { readTextFile } = await import('@tauri-apps/plugin-fs');
    const path = await open({
      title: i18n.t('calendar.importDialogTitle'),
      multiple: false,
      filters: [{ name: 'iCalendar', extensions: ['ics'] }],
    });
    if (path == null) return null;
    return readTextFile(Array.isArray(path) ? path[0] : path);
  }
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.ics,text/calendar';
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve(null);
      reader.readAsText(file);
    };
    input.click();
  });
}

/** Save .ics text via the dialog plugin + writeTextFile (exportSave.js shape). */
export async function saveIcsText(content) {
  if (isTauri()) {
    const { save } = await import('@tauri-apps/plugin-dialog');
    const { writeTextFile } = await import('@tauri-apps/plugin-fs');
    const filePath = await save({
      defaultPath: 'apiboo-calendar.ics',
      title: i18n.t('calendar.exportDialogTitle'),
      filters: [{ name: 'iCalendar', extensions: ['ics'] }],
    });
    if (filePath == null) return false;
    await writeTextFile(filePath, content);
    return true;
  }
  const blob = new Blob([content], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'apiboo-calendar.ics';
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

/** Which events a toolbar filter shows, over the record's `calendar` field. */
export function matchesCalendarFilter(event, calendarFilter) {
  const cal = event.calendar || 'personal';
  if (calendarFilter === 'personal') return cal === 'personal';
  if (calendarFilter === 'team') return cal.startsWith('team:');
  return true;
}

/**
 * Phase 3 role mirror (server truth: team sync push is editor+, viewers are
 * read-only — team_sync_controller.ex require_member + require_editor; the
 * TaskBoardTab roleForTeam/isViewerForTeam shape).
 */
export function roleForTeamId(teams, teamId) {
  const t = (teams || []).find((x) => String(x?.id) === String(teamId));
  if (!t) return null;
  return t.role || (t.is_owner ? 'owner' : null);
}

/**
 * True when the viewer may not edit/move/delete this event: viewer role, OR a
 * team the user has NO resolvable role in (left/deleted team, stale record —
 * reviewer fold-in). An unknown team could never accept a push anyway, so a
 * null role is read-only, not writable-by-default.
 */
export function isReadOnlyEvent(event, teams) {
  const teamId = calendarTeamId(event?.calendar);
  if (teamId == null) return false; // personal (incl. materialized copies)
  const role = roleForTeamId(teams, teamId);
  return role === 'viewer' || role == null;
}

export const isTypingTarget = (el) =>
  el &&
  (el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable);
